import { ModuleRegistry } from '@ag-grid-community/core'
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail'
import {
  Component,
  Inject,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { ResizeHandleState } from '@helios/generic-components'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  ExternalFileFilter,
  RootState,
  selectActiveSavedViews,
  selectActiveSessionReconDate,
  selectExtFileViewerDateRangeFilter,
  selectFirstFocusedAccountInfo,
  selectFocusedExternalFileFilter,
  setExtFileViewerDateRangeFilter,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { toISOFormat } from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'
import { ComponentContainer } from 'golden-layout'
import { ContextMenuComponent } from 'ngx-contextmenu'
import { Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators'
import {
  ExternalFileViewerDetailsGridKey,
  ExternalFileViewerKey,
  ExternalFileViewerMainGridKey,
} from '../panel-key'
import { ExternalFileViewerDetailsComponent } from './external-file-viewer-details/external-file-viewer-details.component'
import { Account, FileDataModel } from './models/external-file-viewer'
import { ExternalFileViewerService } from './services/external-file-viewer.service'

ModuleRegistry.registerModules([MasterDetailModule])

interface ExternalFileViewerPanelState {
  resizeHandleState: ResizeHandleState
}

@Component({
  selector: 'hs-external-file-viewer',
  templateUrl: './external-file-viewer-panel.component.html',
  styleUrls: ['./external-file-viewer-panel.component.scss'],
})
export class ExternalFileViewerPanelComponent
  extends BasePanelComponent<ExternalFileViewerPanelState>
  implements OnDestroy
{
  panelKey = ExternalFileViewerKey
  selectedAccount: Account | undefined
  private readonly _destroyed$ = new Subject<void>()
  dateRangeFilter?: Daterange
  activeTab = 1
  selectedRawDataList: FileDataModel[] = []
  selectedRawDataFilter?: ExternalFileFilter
  private sessionReconDate: string | null = null

  @ViewChild(ContextMenuComponent)
  basicMenu!: ContextMenuComponent
  @ViewChildren(ExternalFileViewerDetailsComponent)
  externalFileViewerDetailsComponent?: QueryList<ExternalFileViewerDetailsComponent>

  mainGridFilter$ = this.store.select(selectActiveSavedViews).pipe(
    // Find the saved view state with the specified grid key
    map(savedViews =>
      savedViews?.find(view => view.gridKey === ExternalFileViewerMainGridKey),
    ),
    // Extract the filter state, defaulting to an empty object if undefined
    map(savedViewState => savedViewState?.savedView.filterState ?? {}),
    // Filter out states that are undefined
    filter(state => state !== undefined),
    // Compare and filter out consecutive identical states
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  )

  fileGridFilters$ = this.store
    .select(selectActiveSavedViews)
    .pipe(
      map(savedViews =>
        savedViews?.filter(view =>
          view.gridKey.includes(ExternalFileViewerDetailsGridKey),
        ),
      ),
    )

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    readonly externalFileViewerService: ExternalFileViewerService,
  ) {
    super(container)
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  ngOnInit(): void {
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
        tap(date => {
          this.sessionReconDate = `${toISOFormat(date)}`
          if (date != null) {
            this.dateRangeFilter = {
              beginDate: toISOFormat(date),
              endDate: toISOFormat(date),
            }
          }
        }),
      )
      .subscribe()

    this.store
      .select(selectExtFileViewerDateRangeFilter)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(
          (a, b) => a.beginDate === b.beginDate && a.endDate === b.endDate,
        ),
        tap(dateRange => {
          if (dateRange.beginDate && dateRange.endDate) {
            this.dateRangeFilter = {
              beginDate: dateRange.beginDate,
              endDate: dateRange.endDate,
            }
          } else {
            this.dateRangeFilter = {
              beginDate: this.sessionReconDate,
              endDate: this.sessionReconDate,
            }
          }
        }),
      )
      .subscribe()

    this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
        tap(selectedAccount => {
          if (selectedAccount != null) {
            this.selectedRawDataList = []
            this.selectedRawDataFilter = undefined
            this.setSelectedAccount(selectedAccount)
          }
        }),
      )
      .subscribe()

    this.store
      .select(selectFocusedExternalFileFilter)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged(),
        tap(rawData => {
          if (rawData != null) {
            this.dateRangeFilter = {
              beginDate: toISOFormat(rawData.reconDate),
              endDate: toISOFormat(rawData.reconDate),
            }
            this.selectedRawDataFilter = rawData
          }
        }),
      )
      .subscribe()
  }

  close(event: MouseEvent, tabToRemove: FileDataModel) {
    this.selectedRawDataList = this.selectedRawDataList.filter(
      row => row !== tabToRemove,
    )
    this.activeTab = this.selectedRawDataList.length
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  closeAll(currentTab?: FileDataModel) {
    if (currentTab) {
      this.selectedRawDataList = this.selectedRawDataList.filter(
        row => row == currentTab,
      )
    } else {
      this.selectedRawDataList = []
    }
    this.activeTab = this.selectedRawDataList.length
  }

  setSelectedAccount(selectedAccount: Account) {
    this.selectedAccount = selectedAccount
  }

  setSelectedRawData(selectedFile: FileDataModel) {
    const fileSelectionPredicateFn = (file: FileDataModel) =>
      file.fileId === selectedFile.fileId &&
      file.fileType === selectedFile.fileType &&
      file.reconDate === selectedFile.reconDate &&
      file.accountId === selectedFile.accountId

    // when file does not exist
    if (!this.selectedRawDataList.find(fileSelectionPredicateFn)) {
      this.selectedRawDataList.push(selectedFile)
      this.activeTab = this.selectedRawDataList.length
    } else {
      // else file exists, switch tab to already loaded file
      this.activeTab =
        this.selectedRawDataList.findIndex(fileSelectionPredicateFn) + 1
    }
  }

  setRawDataFilter(rawData: ExternalFileFilter) {
    this.selectedRawDataFilter = rawData
  }

  setDateRangeFilter(dateRange: Daterange) {
    this.dateRangeFilter = { ...dateRange }
    this.store.dispatch(setExtFileViewerDateRangeFilter(dateRange))
  }

  onResizeFinished(resizeHandleState: ResizeHandleState) {
    this.setState({
      ...this.state,
      resizeHandleState,
    })
  }
  resetSelection() {
    this.externalFileViewerDetailsComponent?.map(childComponent => {
      childComponent.agGridApi?.api.deselectAll()
      childComponent.agGridApi?.api.clearRangeSelection()
    })
  }
}
